import type { BlockPerimeters } from "building-types/blocks/blockTypes";
import { WallLateralDatumAnchor } from "building-types/datums/datumsTypesEnum";
import type { FloorAssembly } from "building-types/floors/floorAssemblyTypes";
import type { RoofHandle } from "building-types/handles/docEntityHandleTypes";
import type { Level } from "building-types/levels/levelTypes";
import type { RemoveFormula } from "building-types/options/formula";
import { isIndexedRoofPeakDirection } from "building-types/roofs/roofSystemTypes";
import type { Roof, RoofSystemTableInternal } from "building-types/roofs/roofSystemTypes";
import type { Room } from "building-types/rooms/roomTypes";
import type { BuildingGenerationIssue } from "building-types/settings/buildingGenerationIssue";
import { BuildingGenerationIssueType } from "building-types/settings/buildingGenerationIssue";
import type { MergeSettingsForEntityParams } from "building-types/settings/mergeSettingsForEntityParams";
import type { RoofSettings } from "building-types/settings/roofSettings";
import { type RoofSystemLevel, type RoofInputPerimeters } from "building-types/settings/roofTypes";
import { RoofPeakDirection, RoofBoundaryType } from "building-types/settings/settingsTypesEnum";
import type { WallSettings } from "building-types/settings/wallSettings";
import { WallType } from "building-types/walls/wallEnums";
import type { WallMassSystem } from "building-types/walls/wallSytemTypes";
import { GABLE_ROOF_THICKNESS } from "common/constants/buildingObjects";
import type { RWTable, Table } from "common/data/table";
import { floorAssemblyTop } from "doc/elements/floor/floorAssembly";
import { isVirtualRoofBlock, resolveRoofBlockIds } from "doc/roofSystemTools";
import { isInteriorRoomEnclosure, isOpenRoomEnclosure, isWalledRoomEnclosure } from "doc/rooms/roomAttributesTools";
import { roomsInBlock } from "doc/rooms/roomsInBlock";
import { MULTIGABLE_BSP_EPSILON, multiGableRoofLevelNew, roofInputPerimetersNew } from "doc/utils/roofTools";
import { getExteriorWallOffset } from "doc/utils/wallTypeTools";
import { aabb2Union, aabb2IsValid } from "geom/aabb2";
import { aabb2New } from "geom/aabb2Types";
import { buildPolygonsFromPolyline2, addPolygonsToBSP } from "geom/bsp";
import { addEdgeToPolygonList } from "geom/bspClipping";
import type { NodeBSP, PolygonBSP } from "geom/bspTypes";
import { classifyContainmentPolyLine2PolyLine2, PolyLine2PolyLine2ContainmentClass } from "geom/containment";
import { WindingDir, DIST_EPSILON, DIST_EPSILON_SQUARED } from "geom/core";
import { intersectLine2Line2, CurveCurveRangeIntersection } from "geom/intersectLine2Line2";
import { offsetPolyLine2 } from "geom/offset";
import {
  planarGraphTracePerimeter,
  planarGraphHalfEdgeStripToPolyLine2,
  planarGraphTraceFaceGroupPerimeter,
} from "geom/planarGraph";
import { planeNewXY, planeReverse } from "geom/plane";
import { polygonUnion } from "geom/polyclip/polyClip";
import { polygonIntersection, polygonSubtract } from "geom/polygon";
import type { PolygonPlanarGraph } from "geom/polygonPlanarGraph";
import { polygonPlanarGraphNew } from "geom/polygonPlanarGraph";
import {
  polyLine2WindingDir,
  polyLine2SetCW,
  polyLine2Copy,
  polyLine2EdgeCount,
  polyLine2EdgeStart,
  polyLine2EdgeEnd,
  polyLine2Reverse,
  polyLine2Area,
  polyLine2SetCCW,
  polyLine2IsClosed,
  polyLine2EdgeTangent,
  polyLine2AABB2,
  polyLine2Rotate,
  polyLine2FromAABB2,
  polyLine2MergeCollinearEdges,
  polyLine2MergeCoincidentVertices,
  polyLine2RemoveInvalidVertices,
  polyLine2ReflectOverYAxis,
  polyLine2ReverseCopy,
} from "geom/polyLine2";
import { PolyLine2 } from "geom/polyLine2Types";
import { vec2Dot, vec2AngleBetween } from "geom/vec2";
import { getPlanarGraphFaces } from "../utils/planarGraphPerimeters";
import { levelBelowId, levelAboveId } from "./level/levelTools";
import { getFreeFormRoofOffsetPerimeter, getRoofOffsetPerimeters } from "./multigableRoofPerimeter";
import { filterRoofBlocks } from "./multiGableRoofTools";
import getMergedRoofSettings from "./roofs/getMergedRoofSettings";
import { getRoofLevelId, getRoofSystemLevelId, getSortedRoofs } from "./roofs/getRoofSystems";
import { hasDiagonalEdge } from "./roofs/roofPerimeters";
import { nonEmptyGableSubRoof } from "./roofs/roofSubRoofFilters";

export function isVirtualRoofBlockCCW(subRoof: Roof) {
  const { blocks: roofBlocks } = subRoof;

  if (!isVirtualRoofBlock(roofBlocks)) {
    return false;
  }

  const { perimeter } = roofBlocks;

  return polyLine2WindingDir(perimeter) === WindingDir.CCW;
}

export function getRoofBasicBlockPerimeters(subRoof: Roof, blockPerimeters: BlockPerimeters): PolyLine2[] {
  const perimeters: PolyLine2[] = [];

  const { blocks: roofBlocks } = subRoof;
  const blockIds = resolveRoofBlockIds(roofBlocks, blockPerimeters);

  if (blockIds.length > 0) {
    const filteredBlocks = filterRoofBlocks(blockIds, blockPerimeters);
    for (let i = 0, l = filteredBlocks.length; i < l; i++) {
      if (blockPerimeters[filteredBlocks[i]]) {
        perimeters.push(...blockPerimeters[filteredBlocks[i]]);
      }
    }
  } else if (isVirtualRoofBlock(roofBlocks)) {
    const { perimeter } = roofBlocks;
    if (!(perimeter instanceof PolyLine2)) {
      console.assert(false, "Reference geometries should be already resolved.");
    }

    perimeters.push(perimeter as PolyLine2);
  }

  return perimeters;
}

function isFloatingVirtualSubRoof(
  roof: Roof,
  planarGraphs: Table<PolygonPlanarGraph>,
  levels: Table<Level>,
  wallMasses: Table<WallMassSystem> | undefined,
): boolean {
  const { blocks: roofBlocks } = roof;

  if (!isVirtualRoofBlock(roofBlocks)) {
    return false;
  }

  if (!(roofBlocks.perimeter instanceof PolyLine2)) {
    console.assert(false, "Reference geometries should be already resolved.");
  }
  const perimeter = polyLine2SetCW(polyLine2Copy(roofBlocks.perimeter as PolyLine2));

  const isFloatingRoof = (levelId: string): boolean => {
    const wallMassSystem = wallMasses ? wallMasses[levelId] : undefined;

    const edgeCount = polyLine2EdgeCount(perimeter);
    let alignCount = 0;
    for (let i = 0; i < edgeCount; i++) {
      const [ax, ay] = polyLine2EdgeStart(perimeter, i);
      const [bx, by] = polyLine2EdgeEnd(perimeter, i);

      // look for aligned walls
      if (wallMassSystem) {
        const { walls } = wallMassSystem;
        for (const wallMassId in walls) {
          const wallMass = wallMassSystem.walls[wallMassId];

          if (!wallMass || wallMass.wallType !== WallType.SOLID_MASS) {
            continue;
          }

          const { path } = wallMass;
          const { startX, startY, endX, endY } = path;

          const intersection = intersectLine2Line2(ax, ay, bx, by, startX, startY, endX, endY, DIST_EPSILON);
          if (intersection && intersection instanceof CurveCurveRangeIntersection) {
            const { leftRoom, rightRoom } = path;
            const isLeft = vec2Dot(bx - ax, by - ay, endX - startX, endY - startY) > 0;
            const room = isLeft ? leftRoom : rightRoom;
            const isExteriorWall = !room || !isWalledRoomEnclosure[room.attributes.roomEnclosure];
            if (isExteriorWall) {
              ++alignCount;
            }
          }
        }
      }
    }

    return !!wallMasses && alignCount === 0;
  };

  const levelId = getRoofLevelId(roof, {}, levels);

  if (isFloatingRoof(levelId)) {
    return true;
  }

  // Filter by roof perimeter not fully inside the bottom level
  const bottomLevelId = levelBelowId(levelId, levels);
  if (bottomLevelId && levels[bottomLevelId]?.floorIndex >= 0) {
    const bottomPlanarGraph = planarGraphs[bottomLevelId];
    const insidePerimeters = planarGraphTracePerimeter(bottomPlanarGraph.planarGraph);

    let outSideCount = 0;
    for (const faceHalfEdges of insidePerimeters.faceHalfEdges) {
      const insidePerimeter = planarGraphHalfEdgeStripToPolyLine2(bottomPlanarGraph.planarGraph, faceHalfEdges);

      const cont = classifyContainmentPolyLine2PolyLine2(perimeter, insidePerimeter);

      if (cont === PolyLine2PolyLine2ContainmentClass.OUTSIDE) {
        outSideCount++;
      }
    }

    return outSideCount === insidePerimeters.faceHalfEdges.length;
  }

  return false;
}

interface GetSubRoofBlockPerimetersOptions {
  readonly handle: RoofHandle;
  readonly subRoof: Roof;
  readonly levels: Table<Level>;
  readonly blockLevelIds: { [blockId: string]: string };
  readonly blockPerimeters: BlockPerimeters;
  readonly rooms: Table<Room>;
  readonly roomLevelIndices: Table<number>;
  readonly polygonPlanarGraphs: Table<PolygonPlanarGraph>;
  readonly wallSettings: WallSettings;
  readonly roofSettings: RoofSettings;
  readonly wallMasses?: Table<WallMassSystem>;
  readonly issues: BuildingGenerationIssue[];
  readonly wallPlaneEnabled?: boolean;
}

function getSubRoofBlockPerimeters({
  handle,
  subRoof,
  levels,
  blockLevelIds,
  blockPerimeters,
  rooms,
  roomLevelIndices,
  polygonPlanarGraphs,
  wallSettings,
  roofSettings,
  wallMasses,
  issues,
  wallPlaneEnabled = false,
}: GetSubRoofBlockPerimetersOptions): PolyLine2[] {
  const perimeters: PolyLine2[] = [];

  const { blocks: roofBlocks } = subRoof;

  const { overhangDatum = WallLateralDatumAnchor.OUTSIDE_FACE_OF_FRAMING } = roofSettings;

  const exteriorWallOffsetFromOverhangDatum = getExteriorWallOffset(wallSettings, overhangDatum);

  const resolvedBlockIds = resolveRoofBlockIds(roofBlocks, blockPerimeters);
  if (resolvedBlockIds.length > 0) {
    for (const blockId of resolvedBlockIds) {
      if (!blockPerimeters[blockId]) {
        continue;
      }

      const blockRooms = roomsInBlock(blockId, rooms);
      const levelId = blockLevelIds[blockId];
      const wallMassSystem = wallMasses ? wallMasses[levelId] : null;
      const polygonPlanarGraph = polygonPlanarGraphs[levelId];

      if (polygonPlanarGraph) {
        const { planarGraph } = polygonPlanarGraph;

        const faceIndices = getPlanarGraphFaces(
          rooms,
          roomLevelIndices,
          blockRooms.map((x) => x.id),
          polygonPlanarGraph,
        );

        // we use -exteriorWallOffsetFromOverhangDatum as the baseOffset because we apply it later
        // and we need to compensate it here
        perimeters.push(
          ...getRoofOffsetPerimeters({
            wallMassSystem,
            planarGraph,
            faceIndices,
            wallSettings,
            overhangDatum,
            baseOffset: -exteriorWallOffsetFromOverhangDatum,
            wallPlaneEnabled,
          }),
        );
      }
    }
  } else if (isVirtualRoofBlock(roofBlocks)) {
    if (!(roofBlocks.perimeter instanceof PolyLine2)) {
      console.assert(false, "Reference geometries should be already resolved.");
    }

    const freeFormPerimeter = polyLine2SetCW(polyLine2Copy(roofBlocks.perimeter as PolyLine2));
    const isValidPerimeter =
      polyLine2EdgeCount(freeFormPerimeter) >= 1 && polyLine2Area(freeFormPerimeter) > DIST_EPSILON_SQUARED;

    if (isValidPerimeter) {
      const levelId = getRoofLevelId(subRoof, blockLevelIds, levels);

      const wallMassSystem = wallMasses ? wallMasses[levelId] : null;
      const polygonPlanarGraph = polygonPlanarGraphs[levelId];

      if (polygonPlanarGraph) {
        // we use -exteriorWallOffsetWithSheathing as the baseOffset because we apply it later
        // and we need to compensate it here
        const { planarGraph } = polygonPlanarGraph;

        perimeters.push(
          getFreeFormRoofOffsetPerimeter({
            wallMassSystem,
            planarGraph,
            freeFormPerimeter,
            wallSettings,
            overhangDatum,
            baseOffset: -exteriorWallOffsetFromOverhangDatum,
            wallPlaneEnabled,
          }),
        );
      }
    } else {
      issues.push({
        type: BuildingGenerationIssueType.INVALID_FREE_FORM_ROOF_PERIMETER,
        handle,
      });
    }
  }

  return perimeters;
}

function getSubRoofExactPerimeterOld(
  currentFloorPerimeter: PolyLine2,
  subRoofPerimeter: PolyLine2,
  clippingPerimeters: PolyLine2[],
): PolyLine2 {
  const intersection = polygonIntersection([[currentFloorPerimeter], [subRoofPerimeter]]);
  const limitPerimeter = intersection.length === 1 && intersection[0].length === 1 ? intersection[0][0] : null;

  const exactPerimeter = limitPerimeter || polyLine2Copy(subRoofPerimeter);

  const outsideMultiPolygons = polygonSubtract([[exactPerimeter, ...clippingPerimeters]]);

  if (outsideMultiPolygons.length === 1 && outsideMultiPolygons[0].length === 1) {
    return polyLine2SetCCW(outsideMultiPolygons[0][0]);
  }

  return polyLine2SetCCW(exactPerimeter);
}

interface GetSubRoofPerimeterOptions {
  readonly handle: RoofHandle;
  readonly subRoof: Roof;
  readonly levels: Table<Level>;
  readonly peakDirection: RoofPeakDirection | undefined;
  readonly blockPerimeters: BlockPerimeters;
  readonly blockLevelIds: { [blockId: string]: string };
  readonly rooms: Table<Room>;
  readonly roomLevelIndices: Table<number>;
  readonly polygonPlanarGraphs: Table<PolygonPlanarGraph>;
  readonly wallSettings: WallSettings;
  readonly roofSettings: RoofSettings;
  readonly mirror: boolean;
  readonly wallMasses?: Table<WallMassSystem>;
  readonly clippingPerimeters?: PolyLine2[];
  readonly issues: BuildingGenerationIssue[];
  readonly wallPlaneEnabled?: boolean;
}

function getSubRoofPerimeter({
  handle,
  subRoof,
  levels,
  peakDirection,
  blockPerimeters,
  blockLevelIds,
  rooms,
  roomLevelIndices,
  polygonPlanarGraphs,
  wallSettings,
  roofSettings,
  mirror,
  wallMasses,
  clippingPerimeters = [],
  issues,
  wallPlaneEnabled = false,
}: GetSubRoofPerimeterOptions): PolyLine2 | null {
  const subRoofBlockPerimeters = getSubRoofBlockPerimeters({
    handle,
    subRoof,
    levels,
    blockLevelIds,
    blockPerimeters,
    rooms,
    roomLevelIndices,
    polygonPlanarGraphs,
    wallSettings,
    roofSettings,
    wallMasses,
    issues,
    wallPlaneEnabled,
  });

  const subRoofBasicBlockPerimeters = getRoofBasicBlockPerimeters(subRoof, blockPerimeters);

  if (subRoofBlockPerimeters.length === 0 || subRoofBasicBlockPerimeters.length === 0) {
    return null;
  }

  const firstPerimeter = subRoofBasicBlockPerimeters[0];

  // determine the first edge of the first block based on the peak direction mode
  let firstEdge = 0;
  if (peakDirection === RoofPeakDirection.BY_LOW_RAIL_INDEX) {
    firstEdge = subRoof.lowRailIndexForPeakDirection ?? 0;
  } else if (peakDirection === RoofPeakDirection.BY_GABLE_END_RAIL_INDEX) {
    firstEdge = (subRoof.lowRailIndexForPeakDirection ?? 0) + 1;
  }

  if (mirror && peakDirection !== undefined && isIndexedRoofPeakDirection[peakDirection]) {
    firstEdge = polyLine2EdgeCount(firstPerimeter) - firstEdge;
    if (!polyLine2IsClosed(firstPerimeter)) {
      firstEdge--;
    }
  }

  // get the angle of the first edge of the first block to use it as the main axis
  const [mainAxisX, mainAxisY] = polyLine2EdgeTangent(firstPerimeter, firstEdge);
  const angle = vec2AngleBetween(1.0, 0.0, mainAxisX, mainAxisY);

  // get the perimeter of the roof system blocks
  // NOTE: we are making the assumption that the shape of a single block perimeter or block group perimeter, is always
  // a rectangle

  let aabox = aabb2New(Number.MAX_VALUE, Number.MAX_VALUE, -Number.MAX_VALUE, -Number.MAX_VALUE);

  for (let j = 0, lj = subRoofBlockPerimeters.length; j < lj; j++) {
    // split the perimeter against the upper level BSP
    const blockPerimeter = polyLine2Copy(subRoofBlockPerimeters[j]);

    const outsideMultiPolygons = polygonSubtract([[blockPerimeter, ...clippingPerimeters]]);

    // iterate the subpolygons after the split
    let uncoveredAABB2 = aabb2New(Number.MAX_VALUE, Number.MAX_VALUE, -Number.MAX_VALUE, -Number.MAX_VALUE);

    for (let k = 0, lk = outsideMultiPolygons.length; k < lk; k++) {
      const outsidePolygons = outsideMultiPolygons[k];
      for (let m = 0, lm = outsidePolygons.length; m < lm; m++) {
        const outsidePolygon = outsidePolygons[m];

        // generate subpolygon AABB
        const outsidePolygonAABB2 = polyLine2AABB2(polyLine2Rotate(outsidePolygon, -angle));
        uncoveredAABB2 = aabb2Union(uncoveredAABB2, outsidePolygonAABB2);
      }
    }

    aabox = aabb2Union(aabox, uncoveredAABB2);
  }

  if (!aabb2IsValid(aabox)) {
    return null;
  }

  return polyLine2Rotate(polyLine2FromAABB2(aabox), angle);
}

interface MakeRoofSystemLevelOptions extends MergeSettingsForEntityParams {
  readonly levelId: string;
  readonly blockLevelIds: { [blockId: string]: string };
  readonly blockPerimeters: BlockPerimeters;
  readonly rooms: Table<Room>;
  readonly roomLevelIndices: Table<number>;
  readonly polygonPlanarGraphs: Table<PolygonPlanarGraph>;
  readonly upperFloorBSP: NodeBSP | null;
  readonly upperFloorRoomsBSP: NodeBSP | null;
  readonly halfStoriesBSP: NodeBSP | null;
  readonly upperFloorPerimeters: RWTable<PolyLine2[]>;
  readonly upperFloorCeilElevation?: number;
  readonly roofSystems: RoofSystemTableInternal;
  readonly roofSettings: RemoveFormula<RoofSettings>;
  readonly wallSettings: WallSettings;
  readonly mirror: boolean;
  readonly wallMasses?: Table<WallMassSystem>;
  readonly levels: Table<Level>;
  readonly issues: BuildingGenerationIssue[];
  readonly wallPlaneEnabled?: boolean;
}

function makeRoofSystemLevel({
  levelId,
  blockLevelIds,
  blockPerimeters,
  rooms,
  roomLevelIndices,
  polygonPlanarGraphs,
  upperFloorBSP,
  upperFloorRoomsBSP,
  halfStoriesBSP,
  upperFloorPerimeters,
  upperFloorCeilElevation,
  roofSystems,
  roofSettings,
  wallSettings,
  wallMasses,
  levels,
  mirror,
  issues,
  entitySettings,
  activeSettingsSets,
  optionChoices,
  settings,
  wallPlaneEnabled = false,
}: MakeRoofSystemLevelOptions): RoofSystemLevel {
  let clippingPerimeters: PolyLine2[] = [];
  let exactClippingPerimeters: PolyLine2[] = [];
  const subGableRoofsPerimeters: RWTable<RoofInputPerimeters> = {};
  const subGableRoofIdToPolygonIndex: RWTable<number> = {};
  const subGablePolygonIndexToRoofId: RWTable<string> = {};
  const subGableRoofPolygons: PolyLine2[] = [];
  const subGableRoofExactPolygons: PolyLine2[] = [];

  for (const roofSystemId in roofSystems) {
    const roofSystem = roofSystems[roofSystemId];

    if (getRoofSystemLevelId(roofSystem, blockLevelIds, levels) !== levelId) {
      continue;
    }

    const filteredSubRoofs = getSortedRoofs(roofSystem, nonEmptyGableSubRoof);

    // iterate through subroofs
    for (const subRoof of filteredSubRoofs) {
      const handle: RoofHandle = { type: "ROOF", id: subRoof.id };
      const { peakDirection, boundaryType } = getMergedRoofSettings({
        roof: subRoof,
        homeSettings: roofSettings,
        entitySettings,
        activeSettingsSets,
        optionChoices,
        settings,
      });
      const isVirtualRoof = isVirtualRoofBlock(subRoof.blocks);
      const isFloatingVirtualRoof = isFloatingVirtualSubRoof(subRoof, polygonPlanarGraphs, levels, wallMasses);

      const { roofVersion, roofEdgeOverrides } = subRoof;

      // get the source perimeter
      const srcPerimeter = getSubRoofPerimeter({
        handle,
        subRoof,
        levels,
        peakDirection,
        blockPerimeters,
        blockLevelIds,
        rooms,
        roomLevelIndices,
        polygonPlanarGraphs,
        wallSettings,
        roofSettings,
        mirror,
        wallMasses,
        issues,
        wallPlaneEnabled,
      });

      if (!srcPerimeter) {
        continue;
      }

      // get the input perimeter
      let inputPerimeter = srcPerimeter;

      if (isVirtualRoofBlock(subRoof.blocks)) {
        if (!(subRoof.blocks.perimeter instanceof PolyLine2)) {
          console.assert(false, "Reference geometries should be already resolved.");
        }
        inputPerimeter = polyLine2Copy(subRoof.blocks.perimeter as PolyLine2);
      }

      // get the planar graph polygons
      const clippedPolygon =
        peakDirection === undefined || !isIndexedRoofPeakDirection[peakDirection]
          ? getSubRoofPerimeter({
              handle,
              subRoof,
              levels,
              peakDirection,
              blockPerimeters,
              blockLevelIds,
              rooms,
              roomLevelIndices,
              polygonPlanarGraphs,
              wallSettings,
              roofSettings,
              clippingPerimeters,
              mirror,
              wallMasses,
              issues,
              wallPlaneEnabled,
            })
          : null;

      const polygonId = clippedPolygon ? subGableRoofPolygons.length : -1;

      // get the perimeter after clipping
      const clippedPerimeter =
        peakDirection === undefined || !isIndexedRoofPeakDirection[peakDirection]
          ? getSubRoofPerimeter({
              handle,
              subRoof,
              levels,
              peakDirection,
              blockPerimeters,
              blockLevelIds,
              rooms,
              roomLevelIndices,
              polygonPlanarGraphs,
              wallSettings,
              roofSettings,
              clippingPerimeters: exactClippingPerimeters,
              mirror,
              wallMasses,
              issues,
              wallPlaneEnabled,
            })
          : null;

      const boundingPerimeter = clippedPerimeter
        ? clippedPerimeter
        : getSubRoofPerimeter({
            handle,
            subRoof,
            levels,
            peakDirection,
            blockPerimeters,
            blockLevelIds,
            rooms,
            roomLevelIndices,
            polygonPlanarGraphs,
            wallSettings,
            roofSettings,
            mirror,
            wallMasses,
            issues,
            wallPlaneEnabled,
          });

      if (!boundingPerimeter) {
        continue;
      }

      let limitPerimeter: PolyLine2 | null = null;
      let exactPerimeter = polyLine2Copy(boundingPerimeter);

      const subRoofBlockPerimeters = getSubRoofBlockPerimeters({
        handle,
        subRoof,
        levels,
        blockLevelIds,
        blockPerimeters,
        rooms,
        roomLevelIndices,
        polygonPlanarGraphs,
        wallSettings,
        roofSettings,
        wallMasses,
        issues,
        wallPlaneEnabled,
      });

      const joinedPerimeters = polygonUnion({ pls: subRoofBlockPerimeters?.map((x) => [x]) ?? [] });
      const joinedPerimeter =
        joinedPerimeters.length > 0 && joinedPerimeters[0].length > 0 ? joinedPerimeters[0][0] : null;

      let bottomPerimeter: PolyLine2 | null = null;
      if (joinedPerimeter) {
        polyLine2SetCCW(joinedPerimeter);
        bottomPerimeter = polyLine2Reverse(polyLine2Copy(joinedPerimeter));
      }

      if (boundaryType === RoofBoundaryType.CLIP_TO_PERIMETER) {
        if (joinedPerimeter) {
          if (
            roofVersion >= 5 ||
            (Object.keys(roofEdgeOverrides).length > 0 && !hasDiagonalEdge(subRoofBlockPerimeters[0]))
          ) {
            exactPerimeter = polyLine2Copy(
              subRoofBlockPerimeters.length === 1 ? polyLine2SetCCW(subRoofBlockPerimeters[0]) : joinedPerimeter,
            );
            limitPerimeter = polyLine2ReverseCopy(exactPerimeter);
          } else {
            limitPerimeter = polyLine2Reverse(polyLine2Copy(joinedPerimeter));
            exactPerimeter = getSubRoofExactPerimeterOld(
              joinedPerimeter,
              boundingPerimeter,
              peakDirection !== undefined && isIndexedRoofPeakDirection[peakDirection] ? [] : exactClippingPerimeters,
            );
          }
        }
      }
      subGableRoofIdToPolygonIndex[subRoof.id] = polygonId;
      subGablePolygonIndexToRoofId[polygonId] = subRoof.id;

      const subRoofPerimetersInfo = roofInputPerimetersNew(
        inputPerimeter,
        srcPerimeter,
        boundingPerimeter,
        exactPerimeter,
        bottomPerimeter,
        limitPerimeter,
        isVirtualRoof,
        isFloatingVirtualRoof,
      );

      if (polygonId >= 0 && !!clippedPolygon) {
        subGableRoofPolygons.push(clippedPolygon);
        subGableRoofExactPolygons.push(exactPerimeter);
      }

      subGableRoofsPerimeters[subRoof.id] = subRoofPerimetersInfo;

      clippingPerimeters = polygonUnion({
        pls: [offsetPolyLine2(boundingPerimeter, DIST_EPSILON), ...clippingPerimeters].map((x) => [x]),
      }).map((x) => x[0]);

      exactClippingPerimeters = polygonUnion({
        pls: [offsetPolyLine2(exactPerimeter, DIST_EPSILON), ...exactClippingPerimeters].map((x) => [x]),
      }).map((x) => x[0]);

      clippingPerimeters.map(fixRoofPerimeter);
      exactClippingPerimeters.map(fixRoofPerimeter);
    }
  }

  return multiGableRoofLevelNew(
    upperFloorBSP,
    upperFloorRoomsBSP,
    halfStoriesBSP,
    upperFloorPerimeters,
    upperFloorCeilElevation,
    subGableRoofsPerimeters,
    polygonPlanarGraphNew(subGableRoofPolygons),
    polygonPlanarGraphNew(subGableRoofExactPolygons),
    subGableRoofIdToPolygonIndex,
    subGablePolygonIndexToRoofId,
  );
}

function fixRoofPerimeter(pl: PolyLine2): PolyLine2 {
  let res = polyLine2MergeCollinearEdges(pl, MULTIGABLE_BSP_EPSILON);
  res = polyLine2MergeCoincidentVertices(res, MULTIGABLE_BSP_EPSILON);
  res = polyLine2RemoveInvalidVertices(res, MULTIGABLE_BSP_EPSILON);

  return res;
}

function updateFloorBSP(
  root: NodeBSP | null,
  perimeter: PolyLine2,
  bottomElevation: number,
  topElevation: number,
  exteriorWallOffsetWithSheathing: number,
  topCapOffset: number,
): NodeBSP | null {
  const offsetPerimeter = offsetPolyLine2(perimeter, exteriorWallOffsetWithSheathing);
  const capPerimeter = polyLine2SetCW(polyLine2Copy(perimeter));

  const bspPolygons: PolygonBSP[] = [];

  for (let i = 0, l = polyLine2EdgeCount(offsetPerimeter); i < l; i++) {
    addEdgeToPolygonList(offsetPerimeter, i, bottomElevation, topElevation, root, bspPolygons, MULTIGABLE_BSP_EPSILON);
  }

  buildPolygonsFromPolyline2(
    polyLine2ReflectOverYAxis(polyLine2ReverseCopy(capPerimeter)),
    planeReverse(planeNewXY(0, 0, bottomElevation)),
    0,
    bspPolygons,
  );

  buildPolygonsFromPolyline2(capPerimeter, planeNewXY(0, 0, topElevation + topCapOffset), 0, bspPolygons);

  return addPolygonsToBSP(root, null, bspPolygons, MULTIGABLE_BSP_EPSILON, -1);
}

interface GenerateRoofSystemLevelOptions extends MergeSettingsForEntityParams {
  readonly levelId: string;
  readonly blockIsHalfStory: { [blockId: string]: boolean };
  readonly blockPerimeters: BlockPerimeters;
  readonly blockLevelIds: { [blockId: string]: string };
  readonly levels: Table<Level>;
  readonly rooms: Table<Room>;
  readonly roomFloorAssemblies: Table<FloorAssembly>;
  readonly roomLevelIndices: Table<number>;
  readonly polygonPlanarGraphs: Table<PolygonPlanarGraph>;
  readonly roofSystems: RoofSystemTableInternal;
  readonly roofSettings: RemoveFormula<RoofSettings>;
  readonly wallSettings: WallSettings;
  readonly mirror: boolean;
  readonly wallMasses?: Table<WallMassSystem>;
  readonly issues?: BuildingGenerationIssue[];
  readonly wallPlaneEnabled?: boolean;
}

export function generateRoofSystemLevel({
  levelId,
  blockPerimeters,
  blockLevelIds,
  blockIsHalfStory,
  levels,
  rooms,
  roomFloorAssemblies,
  roomLevelIndices,
  polygonPlanarGraphs,
  roofSystems,
  roofSettings,
  wallSettings,
  mirror,
  wallMasses,
  issues = [],
  entitySettings,
  activeSettingsSets,
  optionChoices,
  settings,
  wallPlaneEnabled = false,
}: GenerateRoofSystemLevelOptions): RoofSystemLevel {
  let upperFloorBSP: NodeBSP | null = null;
  let upperFloorRoomsBSP: NodeBSP | null = null;
  let halfStoriesBSP: NodeBSP | null = null;
  let upperFloorCeilElevation: number | undefined = undefined;
  const upperFloorPerimeters: RWTable<PolyLine2[]> = {};
  const roomArray = Object.values(rooms);
  const exteriorWallOffsetWithSheathing = getExteriorWallOffset(
    wallSettings,
    WallLateralDatumAnchor.OUTSIDE_FACE_OF_SUBSTRATE,
  );

  const currFloor = levels[levelId];
  if (currFloor) {
    const { floorElevation, topOfWall } = currFloor;

    const floorCeilElevation = floorElevation + topOfWall;

    for (const blockId in blockLevelIds) {
      if (blockLevelIds[blockId] !== levelId || !blockIsHalfStory[blockId]) {
        continue;
      }

      for (const perimeter of blockPerimeters[blockId]) {
        halfStoriesBSP = updateFloorBSP(
          halfStoriesBSP,
          perimeter,
          0.0,
          floorCeilElevation,
          exteriorWallOffsetWithSheathing,
          GABLE_ROOF_THICKNESS,
        );
      }
    }
  }

  const upperIndex = levelAboveId(levelId, levels);
  const upperFloor = upperIndex !== undefined ? levels[upperIndex] : undefined;
  if (upperFloor) {
    const { id: upperFloorId, floorElevation: upperFloorElevation, topOfWall, floorPlateThickness } = upperFloor;
    const polygonPlanarGraph = polygonPlanarGraphs[upperFloorId];

    upperFloorCeilElevation = upperFloorElevation + topOfWall;

    for (const blockId in blockLevelIds) {
      if (blockLevelIds[blockId] !== upperFloorId || blockIsHalfStory[blockId]) {
        continue;
      }

      const processedPerimeters: PolyLine2[] = [];
      const roomsInBlock = roomArray.filter((room) => room.parentId === blockId).filter((x) => rooms[x.id]);
      for (const perimeter of blockPerimeters[blockId]) {
        const groups = splitBlockByFloorElevation({
          perimeter,
          level: upperFloor,
          roomsInBlock,
          roomFloorAssemblies,
          polygonPlanarGraph,
          roomLevelIndices,
        });

        for (const { perimeters, floorElevation } of groups) {
          for (const splitPerimeter of perimeters) {
            upperFloorBSP = updateFloorBSP(
              upperFloorBSP,
              splitPerimeter,
              floorElevation - floorPlateThickness,
              upperFloorCeilElevation,
              exteriorWallOffsetWithSheathing,
              GABLE_ROOF_THICKNESS,
            );
          }
        }

        processedPerimeters.push(offsetPolyLine2(polyLine2SetCW(polyLine2Copy(perimeter)), -DIST_EPSILON));
      }

      for (const room of roomsInBlock) {
        const { attributes, outline } = room;
        if (!isInteriorRoomEnclosure[attributes.roomEnclosure] && !isOpenRoomEnclosure[attributes.roomEnclosure]) {
          continue;
        }

        const roomFloorAssembly = roomFloorAssemblies[room.id];
        const roomFloorElevation = Math.max(floorAssemblyTop(roomFloorAssembly, upperFloor), upperFloorElevation);

        upperFloorRoomsBSP = updateFloorBSP(
          upperFloorRoomsBSP,
          outline,
          roomFloorElevation,
          upperFloorCeilElevation,
          exteriorWallOffsetWithSheathing,
          GABLE_ROOF_THICKNESS - 0.5,
        );
      }

      upperFloorPerimeters[blockId] = processedPerimeters;
    }
  }

  return makeRoofSystemLevel({
    levelId,
    blockLevelIds,
    blockPerimeters,
    rooms,
    roomLevelIndices,
    polygonPlanarGraphs,
    upperFloorBSP,
    upperFloorRoomsBSP,
    halfStoriesBSP,
    upperFloorPerimeters,
    upperFloorCeilElevation,
    roofSystems,
    roofSettings,
    wallSettings,
    mirror,
    levels,
    wallMasses,
    issues,
    entitySettings,
    activeSettingsSets,
    optionChoices,
    settings,
    wallPlaneEnabled,
  });
}

interface SplitBlockByFloorElevationOptions {
  readonly perimeter: PolyLine2;
  readonly level: Level;
  readonly roomsInBlock: Room[];
  readonly roomFloorAssemblies: Table<FloorAssembly>;
  readonly polygonPlanarGraph: PolygonPlanarGraph;
  readonly roomLevelIndices: Table<number>;
}

interface BlockPerimetersGroup {
  readonly perimeters: PolyLine2[];
  readonly floorElevation: number;
}

interface BlockFaceIndicesGroup {
  readonly faceIndices: number[];
  readonly floorElevation: number;
}

function splitBlockByFloorElevation({
  perimeter,
  roomsInBlock,
  roomFloorAssemblies,
  level,
  polygonPlanarGraph,
  roomLevelIndices,
}: SplitBlockByFloorElevationOptions): BlockPerimetersGroup[] {
  // helper function to get a key for the height
  const getHeightKey = (height: number): string => {
    return height.toFixed(3);
  };

  const faceIndicesPerFloorHeight: RWTable<BlockFaceIndicesGroup> = {};
  for (const room of roomsInBlock) {
    const roomFloorAssembly = roomFloorAssemblies[room.id];
    const roomFloorElevation = Math.max(floorAssemblyTop(roomFloorAssembly, level), level.floorElevation);
    const key = getHeightKey(roomFloorElevation);

    // create the group if needed
    if (!faceIndicesPerFloorHeight[key]) {
      faceIndicesPerFloorHeight[key] = {
        floorElevation: roomFloorElevation,
        faceIndices: [],
      };
    }

    // get the room planar graph face index
    // TODO(BDG-1862, https://github.com/higharc/product/pull/19574):
    // Having to reach into polygonToPlanarGraph for guarded access
    // instead of using polygonToPlanarGraphFace is a consistent
    // problematic pattern that needs a systematic fix.
    const faceIndex = polygonPlanarGraph.polygonToPlanarGraph[roomLevelIndices[room.id]]?.[0]?.[0]?.index;
    if (faceIndex === undefined) {
      continue;
    }

    // add the face index to the group
    faceIndicesPerFloorHeight[key].faceIndices.push(faceIndex);
  }

  const floorElevationGroups = Object.values(faceIndicesPerFloorHeight);

  // early return if everything is on the same level and it's equal to the
  // level base floor elevation (to avoid introducing numerical errors)
  if (
    floorElevationGroups.length === 1 &&
    getHeightKey(level.floorElevation) === getHeightKey(floorElevationGroups[0].floorElevation)
  ) {
    return [
      {
        perimeters: [perimeter],
        floorElevation: level.floorElevation,
      },
    ];
  }

  const { planarGraph } = polygonPlanarGraph;

  // convert the face indices to perimeters
  return floorElevationGroups.map(({ faceIndices, floorElevation }) => {
    const loops = planarGraphTraceFaceGroupPerimeter(planarGraph, faceIndices);
    const perimeters = loops.faceHalfEdges
      .map((x) => polyLine2Reverse(planarGraphHalfEdgeStripToPolyLine2(planarGraph, x)))
      .filter((x) => polyLine2WindingDir(x) === WindingDir.CCW);

    return {
      perimeters,
      floorElevation,
    };
  });
}
